/**
 * AdminPerfStats — Feb 2026 · R293
 *
 * Rolling API perf dashboard. Shows every route handled by this process
 * since boot, ranked by total time consumed (count × mean) so the biggest
 * tuning wins float to the top. Reads from the admin-only endpoint
 * `GET /api/v1/admin/perf-stats`.
 */
import { useEffect, useState, useCallback } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { apiErrorMessage } from "@/lib/apiError";
import { Button } from "@/components/ui/button";
import { Activity, RefreshCw, Trash2, Clock3, AlertTriangle } from "lucide-react";

const fmt = (n) => (n == null ? "—" : (typeof n === "number" ? n.toLocaleString() : n));
const bucketClr = (ms) =>
  ms <= 50   ? "text-emerald-600" :
  ms <= 200  ? "text-sky-600"     :
  ms <= 500  ? "text-amber-600"   :
  ms <= 1000 ? "text-orange-600"  :
                 "text-rose-600";

export default function AdminPerfStats() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [autoRefresh, setAutoRefresh] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data: body } = await api.get(`/admin/perf-stats?top=100`);
      setData(body);
    } catch (e) {
      toast.error(apiErrorMessage(e, "Failed to load perf stats"));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (!autoRefresh) return;
    const t = setInterval(load, 10_000);
    return () => clearInterval(t);
  }, [autoRefresh, load]);

  const reset = async () => {
    if (!window.confirm("Reset the perf counters for this worker?")) return;
    try {
      await api.post(`/admin/perf-stats/reset`);
      toast.success("Counters cleared");
      load();
    } catch (e) {
      toast.error(apiErrorMessage(e, "Reset failed"));
    }
  };

  return (
    <div className="p-6 space-y-4" data-testid="admin-perf-stats">
      <div className="flex items-center gap-2 flex-wrap">
        <div className="flex items-center gap-2">
          <Activity className="w-4 h-4 text-primary" />
          <h1 className="text-2xl font-black">API perf</h1>
          {data && (
            <span className="chip chip-internal text-[10px]"
                  title={`Boot: ${new Date(data.boot_ts * 1000).toLocaleString()}`}>
              <Clock3 className="w-3 h-3 inline mr-1" />
              uptime {fmtDuration(data.uptime_s)} · {fmt(data.total_requests)} requests
            </span>
          )}
        </div>
        <div className="ml-auto flex items-center gap-2">
          <label className="inline-flex items-center gap-1 text-[11px] cursor-pointer select-none"
                 data-testid="perf-auto-refresh">
            <input
              type="checkbox"
              className="rounded-none accent-primary"
              checked={autoRefresh}
              onChange={(e) => setAutoRefresh(e.target.checked)}
            />
            Auto-refresh (10s)
          </label>
          <Button size="sm" variant="outline" onClick={load} disabled={loading}
                  data-testid="perf-refresh-btn">
            <RefreshCw className={`w-3.5 h-3.5 mr-1 ${loading ? "animate-spin" : ""}`} /> Refresh
          </Button>
          <Button size="sm" variant="outline" onClick={reset}
                  data-testid="perf-reset-btn">
            <Trash2 className="w-3.5 h-3.5 mr-1" /> Reset
          </Button>
        </div>
      </div>

      <div className="border border-amber-500/30 bg-amber-50 dark:bg-amber-900/10 text-amber-900 dark:text-amber-200 px-3 py-2 text-[11px] flex gap-2 items-start"
           data-testid="perf-note">
        <AlertTriangle className="w-3.5 h-3.5 mt-0.5 shrink-0" />
        <div>
          Numbers are <b>per-worker</b> and reset on pod restart. Rank by "cumulative time" to find the biggest tuning wins — a slow endpoint called twice matters less than a fast one called 500×.
        </div>
      </div>

      {!data ? (
        <div className="text-sm text-muted-foreground italic">Loading…</div>
      ) : data.rows.length === 0 ? (
        <div className="text-sm text-muted-foreground italic border border-dashed border-border p-4">
          No API traffic recorded yet. Open any page in another tab and refresh.
        </div>
      ) : (
        <div className="overflow-auto border border-border">
          <table className="w-full text-[11px]" data-testid="perf-table">
            <thead className="bg-muted/60 border-b border-border text-[9px] uppercase text-muted-foreground">
              <tr>
                <th className="px-2 py-1.5 text-left">Method</th>
                <th className="px-2 py-1.5 text-left">Route</th>
                <th className="px-2 py-1.5 text-right">Count</th>
                <th className="px-2 py-1.5 text-right">Errors</th>
                <th className="px-2 py-1.5 text-right">Mean (ms)</th>
                <th className="px-2 py-1.5 text-right">Max (ms)</th>
                <th className="px-2 py-1.5 text-right" title="Cumulative = count × mean">Cumulative (s)</th>
                <th className="px-2 py-1.5 text-center" title="count by response time bucket">≤50</th>
                <th className="px-2 py-1.5 text-center">≤200</th>
                <th className="px-2 py-1.5 text-center">≤500</th>
                <th className="px-2 py-1.5 text-center">≤1s</th>
                <th className="px-2 py-1.5 text-center">≤2s</th>
                <th className="px-2 py-1.5 text-center text-rose-600" title="Slower than 2s">&gt;2s</th>
              </tr>
            </thead>
            <tbody>
              {data.rows.map((r, i) => {
                const cum_s = (r.mean_ms * r.count) / 1000;
                return (
                  <tr key={i} className="border-b border-border/40 hover:bg-primary/5"
                      data-testid={`perf-row-${i}`}>
                    <td className="px-2 py-1 font-mono text-[10px]">{r.method}</td>
                    <td className="px-2 py-1 font-mono text-[10px] truncate max-w-[320px]" title={r.path}>{r.path}</td>
                    <td className="px-2 py-1 text-right">{fmt(r.count)}</td>
                    <td className={`px-2 py-1 text-right ${r.error_count ? "text-rose-600 font-bold" : "text-muted-foreground"}`}>{fmt(r.error_count)}</td>
                    <td className={`px-2 py-1 text-right font-mono ${bucketClr(r.mean_ms)}`}>{r.mean_ms.toFixed(1)}</td>
                    <td className={`px-2 py-1 text-right font-mono ${bucketClr(r.max_ms)}`}>{r.max_ms.toFixed(1)}</td>
                    <td className="px-2 py-1 text-right font-mono font-bold" title={`${(cum_s*1000).toFixed(0)} ms`}>{cum_s.toFixed(2)}</td>
                    <td className="px-2 py-1 text-center text-emerald-600">{r.p_le_50 || "·"}</td>
                    <td className="px-2 py-1 text-center text-sky-600">{r.p_le_200 || "·"}</td>
                    <td className="px-2 py-1 text-center text-amber-600">{r.p_le_500 || "·"}</td>
                    <td className="px-2 py-1 text-center text-orange-600">{r.p_le_1000 || "·"}</td>
                    <td className="px-2 py-1 text-center text-rose-500">{r.p_le_2000 || "·"}</td>
                    <td className="px-2 py-1 text-center font-bold text-rose-700">{r.p_slow || "·"}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function fmtDuration(sec) {
  if (!sec) return "0s";
  if (sec < 60) return `${Math.round(sec)}s`;
  if (sec < 3600) return `${Math.round(sec / 60)}m`;
  if (sec < 86400) return `${(sec / 3600).toFixed(1)}h`;
  return `${(sec / 86400).toFixed(1)}d`;
}
